import React from 'react';
// ErrorBoundary must NEVER depend on external packages that could fail to resolve.
// All icons are inline SVG to guarantee zero external dependencies.

const IconAlert = (props: { size?: number; className?: string }) => (
  <svg width={props.size || 28} height={props.size || 28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconRefresh = (props: { size?: number; className?: string }) => (
  <svg width={props.size || 12} height={props.size || 12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0118.8-4.3M22 12.5a10 10 0 01-18.8 4.2" />
  </svg>
);

const IconHome = (props: { size?: number; className?: string }) => (
  <svg width={props.size || 12} height={props.size || 12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

// Lazy-load translations with fallback — no external dependency on render path
const fallbackT = {
  errorSignalInterrupted: '信号中断',
  errorRenderFailure: '组件渲染失败',
  errorRetry: '重试',
  errorReload: '重载',
  errorUnknown: '未知错误',
  errorStackDetail: '🔴 堆栈详情（点击展开）',
  errorCopyStack: '复制堆栈',
};

// P2-3 fix: cache translations at module load time to avoid require() in ESM
let _cachedT: Record<string, string> | null = null;
function getT() {
  if (_cachedT) return _cachedT;
  try {
    // In ESM, require() is unavailable; use already-imported translations if available
    // This fallback returns the static fallbackT which is always safe
    const lang = typeof window !== 'undefined' ? (localStorage.getItem('xf-lang') || 'zh') : 'zh';
    _cachedT = fallbackT;
    return _cachedT;
  } catch {
    return fallbackT;
  }
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  // 🔴 2026-07-17 v3.6.60: 保存 componentStack 让用户能复制完整堆栈
  componentStack: string | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const stack = errorInfo.componentStack?.slice(0, 2000) || '';
    console.error('[ErrorBoundary]', error.message, '\n', stack);
    this.setState({ componentStack: stack });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };
  // 🔴 2026-07-17 v3.6.60: 复制堆栈到剪贴板
  handleCopyStack = () => {
    const text = [`[Error]`, this.state.error?.message, '', '[Component Stack]', this.state.componentStack || '(empty)'].join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(
          () => console.log('[ErrorBoundary] stack copied'),
          (e) => console.warn('[ErrorBoundary] clipboard failed', e)
        );
      } else {
        // fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        if (document.body.contains(ta)) document.body.removeChild(ta);
      }
    } catch (e) {
      console.warn('[ErrorBoundary] copy failed', e);
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      let t: typeof fallbackT;
      try {
        t = getT();
      } catch {
        t = fallbackT;
      }

      try {
        return (
          <div
            style={{ opacity: 0, transform: 'scale(0.95)', transition: 'all 0.2s ease', animation: 'efb-in 0.2s forwards' }}
            className="flex items-center justify-center min-h-[60vh]"
          >
            <style>{'@keyframes efb-in { to { opacity: 1; transform: scale(1); } }'}</style>
            <div className="glass-dark rounded-[2.5rem] p-8 border border-white/10 max-w-sm text-center space-y-5">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
                <IconAlert size={28} className="text-red-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-black uppercase tracking-widest text-white/60">{t.errorSignalInterrupted}</h3>
                <p className="text-xs text-white/45 font-mono uppercase tracking-widest">{t.errorRenderFailure}</p>
              </div>
              <p className="text-xs text-white/40 font-mono bg-white/5 rounded-xl p-3 max-h-20 overflow-auto break-all">
                {this.state.error?.message?.slice(0, 200) || t.errorUnknown}
              </p>
              {this.state.componentStack && (
                <details className="text-left">
                  <summary className="text-[10px] text-white/30 cursor-pointer font-mono uppercase tracking-widest hover:text-white/50">
                    🔴 {t.errorStackDetail?.replace('🔴 ', '') || '堆栈详情（点击展开）'}
                  </summary>
                  <pre className="text-[10px] text-white/40 font-mono bg-white/5 rounded-xl p-3 mt-2 max-h-40 overflow-auto break-all whitespace-pre-wrap">
                    {this.state.componentStack}
                  </pre>
                </details>
              )}
              <div className="flex gap-2 justify-center flex-wrap">
                {this.state.componentStack && (
                  <button
                    onClick={this.handleCopyStack}
                    className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {t.errorCopyStack || '复制堆栈'}
                  </button>
                )}
                <button
                  onClick={this.handleRetry}
                  className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <IconRefresh size={12} />
                  {t.errorRetry}
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-5 py-2.5 rounded-xl bg-celestial-saturn/10 border border-celestial-saturn/20 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-celestial-saturn hover:bg-celestial-saturn/20 transition-colors"
                >
                  <IconHome size={12} />
                  {t.errorReload}
                </button>
              </div>
            </div>
          </div>
        );
      } catch (renderErr) {
        // Absolute last resort — pure HTML fallback if even React rendering fails
        return React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: 'monospace', color: '#fff', textAlign: 'center', padding: '2rem' },
        },
          React.createElement('div', null,
            React.createElement('h3', { style: { fontSize: '14px', marginBottom: '8px' } }, t.errorSignalInterrupted),
            React.createElement('p', { style: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' } }, this.state.error?.message || t.errorUnknown),
            React.createElement('button', {
              onClick: () => window.location.reload(),
              style: { padding: '8px 20px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', fontSize: '12px' },
            }, t.errorReload)
          )
        );
      }
    }

    return this.props.children;
  }
}
